<template>
    <div
        class="pdf-wrapper"
        :class="{ drawing: drawMode, 'cross-chat': isCrossChatView }"
    >
        <teleport defer to="#pdf-controls" v-if="!props.hideToolbar">
            <primevue-button text v-tooltip="'Toggle Fullscreen'" @click="openFullScreen">
                <font-awesome-icon :icon="['fas', 'expand']" />
            </primevue-button>

            <primevue-button text v-tooltip="'Download'" @click="download">
                <font-awesome-icon :icon="['fas', 'download']" />
            </primevue-button>

            <primevue-button text v-tooltip="'Zoom In'" @click="zoomIn">
                <font-awesome-icon :icon="['fas', 'magnifying-glass-plus']" />
            </primevue-button>

            <primevue-button text v-tooltip="'Zoom Out'" @click="zoomOut">
                <font-awesome-icon :icon="['fas', 'magnifying-glass-minus']" />
            </primevue-button>
        </teleport>
        <template v-if="props.file">
            <div
                class="pdf"
                :class="{
                    draggable: (Math.round(scale * 10) / 10) > (Math.round(fitScale * 10) / 10),
                    dragging: isDragging,
                    fullScreen: isFullScreen,
                    light: uiStore.theme === 'light',
                }"
                ref="pdfElement"
                @scroll="handleScroll"
                @mousedown="onMouseDown"
                @mouseup="onMouseUp"
                @mouseleave="onMouseUp"
                @focusout="onMouseUp"
                @mousemove="onMouseMove"
            >
                <div class="drawn-box" id="box" v-if="drawMode" />
            </div>
            <div class="block-top" :style="blockTopStyle" />
            <div class="block-bottom" :style="blockBottomStyle" />
            <div class="block-left" :style="blockLeftStyle" />
            <div class="block-right" :style="blockRightStyle" />
        </template>
        <div v-if="props.isLocked">
            <div class="locked-overlay">
                <div class="locked-text">
                    <font-awesome-icon :icon="['fas', 'lock-keyhole']" />
                    <span>This document is locked. Subscribe to access this document.</span>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>

import * as pdfjsLib from 'pdfjs-dist';
import { PDFDocumentProxy } from 'pdfjs-dist';
// eslint-disable-next-line import/extensions, import/no-unresolved
import pdfWorkerUrl from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

import { Vibrant } from 'node-vibrant/browser';

import PrimevueButton from 'primevue/button';
import type { RenderParameters, TextItem, RefProxy } from 'pdfjs-dist/types/src/display/api.d.ts';
import {
    nextTick, onMounted, ref, watch, computed,
} from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useRoute } from 'vue-router';
import { annotate } from 'rough-notation';
import { useDeltaGptStore } from '@/stores/deltaGpt/deltaGptStore.ts';
import { useUIStore } from '@/stores/uiStore.ts';
import { sectionRoutes } from '@/routes/sectionRoutes/sectionRoutes.ts';
import { SectionRouteNames } from '@/routes/sectionRoutes/sectionRouteNames.ts';
import { downloadBlob } from '@/utils/downloadUtils.ts';
import { getRelativeLuminance } from '@/utils/colorUtils.ts';
import { formatPublicationDateForDisplay } from '@/utils/sparkContentUtils.ts';

interface PdfDestination extends Array<unknown> {
    0: { num: number; gen: number };
    1: string | { name: string };
    2?: number | null;
    3?: number | null;
    4?: number | null;
}

const route = useRoute();

const deltaGptStore = useDeltaGptStore();
const uiStore = useUIStore();

const props = defineProps<{
    highlightedTextLocation: string;
    file: Uint8Array;
    downloadFile?: Uint8Array;
    isLocked?: boolean;
    pageDetails?: Record<string, Uint8Array>;
    textItems?: Record<string, Array<TextItem>>;
    pageScaleOverride?: number;
    title?: string;
    hideToolbar?: boolean;
    clearContextCounter?: number;
    scrollRef: HTMLDivElement | null;
    headerHeight?: number;
    addLimitedAccessPage?: boolean;
}>();

const emit = defineEmits<{
    'pdf-loaded': [string];
    'explain': [string];
    'image': [string];
    'summarise': [string];
    'clear-highlights': [];
    'close': [];
    'base-color': [string | null];
    'secondary-color': [string | null];
    'watermark-colours': [Array<string | null>];
    'page-change': [number];
}>();

const blockTopStyle = ref<{ height?: string; top?: string; bottom?: string }>({ height: '0', top: '0' });
const blockBottomStyle = ref<{ height?: string; top?: string; bottom?: string }>({ height: '0', top: '0' });
const blockLeftStyle = ref<{ height?: string; top?: string; left?: string; width?: string }>({ left: '0', width: '0' });
const blockRightStyle = ref<{ height?: string; top?: string; right?: string; width?: string }>({ right: '0', width: '0' });

const PAGE_PADDING = 10;

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;

const pdfElement = ref<HTMLDivElement | null>(null);
const suppressPageChange = ref<boolean>(false);
const pages: Array<pdfjsLib.PDFPageProxy> = [];
let pdf: PDFDocumentProxy | null = null;
const allTextContent: Record<number, string> = {};
const scale = ref(99);
const isScaling = ref(false);
const fitScale = ref(99);
const pageCount = ref(0);
const previousZoomState = ref(1);
const isFullScreen = ref(false);
const drawMode = ref(false);

const highlights: Array<{
    show (): void;
    hide (): void;
    remove (): void;
}> = [];

const accumulatedZooms = ref<number>(0);

const isDragging = ref(false);
const startX = ref(0);
const scrollLeft = ref(0);

const isCrossChatView = computed<boolean>(() => !!route.meta.isCrossChat);

async function refreshPageBaseColors (): Promise<void> {
    if (!pages.length || !pdfElement.value) return;

    const canvas = pdfElement.value.querySelector('.page-wrapper canvas') as HTMLCanvasElement | null;
    if (!canvas) return;

    const { base, secondary } = await getPageThemeColours(canvas);

    emit('base-color', base || null);
    emit('secondary-color', secondary || null);
}

async function collectWatermarkColours (): Promise<void> {
    if (!pages.length || !pdfElement.value) return;

    const pageWrappers = pdfElement.value.querySelectorAll('.page-wrapper');
    const watermarkColours: Array<string | null> = [];

    for (let i = 0; i < pageWrappers.length; i += 1) {
        const pageWrapper = pageWrappers[i] as HTMLElement;
        const canvas = pageWrapper.querySelector('canvas') as HTMLCanvasElement | null;
        if (!canvas) {
            watermarkColours.push(null);
            continue;
        }

        watermarkColours.push(getContrastGreyForWatermark(canvas));
    }

    emit('watermark-colours', watermarkColours);
}

async function getPageThemeColours (canvas: HTMLCanvasElement): Promise<{ base?: string; secondary?: string }> {
    const dataUrl = canvas.toDataURL('image/png');
    const palette = await Vibrant.from(dataUrl).getPalette();
    const isDarkTheme = uiStore.theme === 'dark';

    return {
        base: isDarkTheme ? palette.DarkVibrant?.hex : palette.LightVibrant?.hex,
        secondary: isDarkTheme ? palette.DarkMuted?.hex : palette.LightMuted?.hex,
    };
}

function getContrastGreyForWatermark (canvas: HTMLCanvasElement): string {
    const context = canvas.getContext('2d');
    if (!context) return '#eaeaea';

    const sampleWidth = Math.max(1, Math.floor(canvas.width * 0.32));
    const sampleHeight = Math.max(1, Math.floor(canvas.height * 0.06));
    const imageData = context.getImageData(0, 0, sampleWidth, sampleHeight).data;

    let totalLuminance = 0;
    let pixelCount = 0;

    for (let i = 0; i < imageData.length; i += 4) {
        const r = imageData[i] ?? 0;
        const g = imageData[i + 1] ?? 0;
        const b = imageData[i + 2] ?? 0;
        const alpha = imageData[i + 3] ?? 255;
        if (alpha === 0) continue;

        totalLuminance += getRelativeLuminance(r, g, b);
        pixelCount += 1;
    }

    if (!pixelCount) return '#eaeaea';

    const averageLuminance = totalLuminance / pixelCount;
    return averageLuminance >= 0.78 ? '#575757' : '#c0c0c0';
}

function getCurrentPageNumber () : number|null {
    if (!pdfElement.value) return null;

    const pageWrappers = pdfElement.value?.querySelectorAll('.page-wrapper');
    if (!pageWrappers) return null;

    const viewerRect = pdfElement.value.getBoundingClientRect();
    const windowFrame = { top: viewerRect.top, bottom: viewerRect.top + pdfElement.value.clientHeight };

    let maxVisible = -1;
    let currentPageIndex = 0;
    for (let i = 0; i < pageWrappers.length; i += 1) {
        const div = pageWrappers[i] as HTMLElement;

        const rect = div.getBoundingClientRect();
        const visibleHeight = Math.max(
            0,
            Math.min(rect.bottom, windowFrame.bottom) - Math.max(rect.top, windowFrame.top),
        );
        if (visibleHeight > maxVisible) {
            maxVisible = visibleHeight;
            currentPageIndex = i;
        }
    }

    if (currentPageIndex < 0 || currentPageIndex >= pageWrappers.length) return null;
    return currentPageIndex + 1;
}

function handleScroll (): void {
    if (suppressPageChange.value) {
        clearPageChangeSuppression();
        return;
    }
    emitPageChangeAfterScrollStops();
}

const emitPageChangeAfterScrollStops = useDebounceFn(() => {
    const currentPageNumber = getCurrentPageNumber();
    if (currentPageNumber !== null) emit('page-change', currentPageNumber);
}, 150);

const clearPageChangeSuppression = useDebounceFn(() => {
    suppressPageChange.value = false;
}, 200);

function scrollToPage (pageNumber: number, suppressChange = false): void {
    suppressPageChange.value = suppressChange;
    if (suppressChange) clearPageChangeSuppression();
    scrollToPageWithPosition(pageNumber);
}

async function openFullScreen (): Promise<void> {
    if (!pdfElement.value) return;
    isFullScreen.value = true;
    previousZoomState.value = scale.value;
    await pdfElement.value.requestFullscreen();
}

function zoomIn (): void {
    if (scale.value >= 2) return;
    zoom(0.1);
}

function zoomOut (): void {
    if (scale.value <= 0.1) return;
    zoom(-0.1);
}

async function zoom (zoomScale: number): Promise<void> {
    if (zoomScale > 0) {
        accumulatedZooms.value += 1;
    } else {
        accumulatedZooms.value -= 1;
    }

    if (isScaling.value) return;

    isScaling.value = true;
    while (accumulatedZooms.value !== 0) {
        if ((accumulatedZooms.value < 0 && zoomScale > 0) || (accumulatedZooms.value > 0 && zoomScale < 0)) {
            break;
        }
        const newScale = Math.round((scale.value + zoomScale) * 10) / 10;
        const previousScale = scale.value;
        scale.value = newScale;

        // Prevent accumulated zooms sneaking past the bounds on zoomIn and zoomOut
        if (newScale <= 0.1 || newScale >= 2) {
            accumulatedZooms.value = 0;
            break;
        }

        await applyScale(newScale, previousScale);

        if (accumulatedZooms.value > 0) accumulatedZooms.value -= 1;
        else if (accumulatedZooms.value < 0) accumulatedZooms.value += 1;

        await new Promise<void>(resolve => { setTimeout(resolve, 50); });
    }

    isScaling.value = false;
}

const fileBuffer = ref<Uint8Array | ArrayBuffer | null>(null);

function download (): void {
    const file = props.downloadFile || props.file;
    if (!file) {
        throw new Error('No file provided');
    }
    downloadBlob(new Blob([new Uint8Array(file as unknown as ArrayBuffer)], { type: 'application/pdf' }), props.title ? `${props.title}.pdf` : 'document.pdf');
}

async function fetchPdf (): Promise<PDFDocumentProxy> {
    if (!props.file) {
        throw new Error('No file provided');
    }
    fileBuffer.value = structuredClone(props.file);
    const loader = await pdfjsLib.getDocument({ data: new Uint8Array(fileBuffer.value) });
    return loader.promise;
}

function onMouseDown (e: MouseEvent): void {
    if (!pdfElement.value) return;

    isDragging.value = true;

    startX.value = e.pageX - pdfElement.value.offsetLeft;
    scrollLeft.value = pdfElement.value.scrollLeft;
}

function onMouseUp (): void {
    isDragging.value = false;
}

function onMouseMove (e: MouseEvent): void {
    if (!isDragging.value || !pdfElement.value) return;

    e.preventDefault();
    const x = e.pageX - pdfElement.value.offsetLeft;

    const walkX = (x - startX.value) * 2;

    pdfElement.value.scrollLeft = scrollLeft.value - walkX;
}

async function drawPage (pageNumber: number, includeBlur?: boolean): Promise<void> {
    const page = await pdf?.getPage(pageNumber);
    if (!page) return;
    const width = page.view[2];
    const pageScale = ((props.pageScaleOverride || pdfElement.value!.offsetWidth) - PAGE_PADDING) / width;
    if (pageScale < fitScale.value) {
        fitScale.value = scale.value;
        scale.value = pageScale;
    }

    const viewport = page.getViewport({ scale: scale.value });

    if (props.pageDetails) {
        const pageDetailsWrapper = document.createElement('div');
        pageDetailsWrapper.className = 'page-details-wrapper';
        const gotoDocumentButton = document.createElement('button');
        const titleSpan = document.createElement('span');
        gotoDocumentButton.className = 'p-button p-component plain-btn';
        gotoDocumentButton.title = 'Go to document';
        gotoDocumentButton.innerHTML = '<span>Go to document</span>';

        const pageDetailIndex = pageNumber - 1;
        if (Object.keys(props.pageDetails)[pageDetailIndex]) {
            const documentShortCode = Object.keys(props.pageDetails)[pageDetailIndex].split('_')[0];
            gotoDocumentButton.onclick = (): void => {
                emit('close');
                window.open(`${sectionRoutes[SectionRouteNames.powerAndStorage].path}/research/document/${documentShortCode}`, '_blank', 'noopener,noreferrer');
            };

            const document = deltaGptStore.allDocumentsSimple ? deltaGptStore.allDocumentsSimple.find((doc) => doc.documentShortCode === documentShortCode) : null;

            if (document) {
                titleSpan.textContent = `${document.documentTitle} - ${formatPublicationDateForDisplay(document.publicationDate)}`;
            } else {
                titleSpan.textContent = 'Document not found';
            }
        }
        pageDetailsWrapper.appendChild(titleSpan);
        pageDetailsWrapper.appendChild(gotoDocumentButton);
        pdfElement.value?.appendChild(pageDetailsWrapper);
    }
    const pageWrapper = document.createElement('div');
    pageWrapper.className = includeBlur ? 'page-wrapper blur' : 'page-wrapper';
    pageWrapper.style.position = 'relative';

    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d')!;

    canvas.height = viewport.height;
    canvas.width = viewport.width;

    canvas.style.height = `${viewport.height}px`;
    canvas.style.width = `${viewport.width}px`;

    pageWrapper.appendChild(canvas);
    pdfElement.value?.appendChild(pageWrapper);

    // Render PDF page into canvas context
    const renderContext: RenderParameters = {
        canvasContext: context,
        viewport,
    };

    await (page.render(renderContext)).promise;

    // Render text layer
    const textContent = await page.getTextContent();
    const defaultFont = (textContent.items.find((item) => (item as TextItem).fontName) as TextItem).fontName;

    if (props.textItems?.[pageNumber - 1]) {
        textContent.items = props.textItems[pageNumber - 1] as Array<TextItem>;
        textContent.items.forEach((item) => {
            (item as TextItem).fontName = defaultFont;
        });
    }

    const textLayer = document.createElement('div');
    textLayer.className = 'textLayer';

    textLayer.style.left = `${canvas.offsetLeft}px`;
    textLayer.style.top = `${canvas.offsetTop}px`;
    textLayer.style.height = `${canvas.offsetHeight}px`;
    textLayer.style.width = `${canvas.offsetWidth}px`;

    textLayer.style.setProperty('--scale-factor', scale.value.toString());
    const textLayerRenderer = new pdfjsLib.TextLayer({
        textContentSource: textContent,
        container: textLayer,
        viewport,
    });
    await textLayerRenderer.render();

    pageWrapper.appendChild(textLayer);
    const annotations = await page.getAnnotations({ intent: 'any' });
    annotations.forEach((annotation, i) => {
        createPdfLink(annotation, i, viewport, textLayer);
    });

    pages.push(page);

    allTextContent[pageNumber] = textContent.items
        .filter(item => (item as TextItem).width || (item as TextItem).height)
        .map((item, i) => `{${pageNumber}:${i}}${(item as TextItem).str ?? ''}`)
        .join(' ');
}

function highlightText (): void {
    clearHighlights(false);
    if (!props.highlightedTextLocation) return;
    const [page, sentences] = props.highlightedTextLocation.split(':');
    const splitPage = page.split('_');
    const isSplitPage = splitPage.length > 1;
    const [start, end] = sentences.split('-');
    const pageWrappers = pdfElement.value?.querySelectorAll('.page-wrapper');
    if (!pageWrappers) return;
    const pageWrapper: HTMLElement = pageWrappers[Number(isSplitPage ? splitPage[1] : page)] as HTMLElement;
    const textLayer = pageWrapper?.querySelector('.textLayer');
    const textDivs = textLayer?.querySelectorAll('span');

    for (let i = Number(start); i <= Number(end); i += 1) {
        const textDiv = textDivs?.[i] as HTMLElement;
        if (!textDiv) {
            continue;
        }
        textDiv?.classList.add('highlighted');
        const annotation = annotate(textDiv, {
            type: 'highlight',
            color: '#ff0000',
            strokeWidth: 1,
            animationDuration: 1500,
        });
        annotation.show();
        highlights.push(annotation);
    }
    let topMostCoordinate = Infinity;
    let leftMostCoordinate = Infinity;
    let bottomMostCoordinate = -Infinity;
    let rightMostCoordinate = -Infinity;
    let textHeight = 0;
    for (let i = Number(start); i <= Number(end); i += 1) {
        let textDiv = textDivs?.[i] as HTMLElement;
        if (!textDiv?.style?.top && !textDiv?.style?.left) {
            textDiv = textDivs?.[i + 1] as HTMLElement;
        }
        if (!textDiv?.style?.top) continue;
        const top = Number(textDiv.style.top.replace('%', ''));
        const left = Number(textDiv.style.left.replace('%', ''));
        const width = ((textDiv.getBoundingClientRect().width) / pageWrapper.offsetWidth) * 100;
        if (top < topMostCoordinate) {
            topMostCoordinate = top;
        }
        if (left < leftMostCoordinate) {
            leftMostCoordinate = left;
        }
        if (top > bottomMostCoordinate) {
            bottomMostCoordinate = top;
            if (textHeight === 0) {
                textHeight = textDiv.offsetHeight;
            }
        }
        if (left + width > rightMostCoordinate) {
            rightMostCoordinate = left + width;
        }
    }
    const topBlockHeight = 60;
    const topMostPosition = ((topMostCoordinate / 100) * pageWrapper.offsetHeight) + (pageWrapper.offsetTop ?? 0);

    scrollTo(
        topMostPosition - topBlockHeight,
        ((leftMostCoordinate / 100) * pageWrapper.offsetWidth) + (pageWrapper.offsetLeft ?? 0) - textHeight,
    );
}

async function drawAllPages (): Promise<void> {
    pdf = await fetchPdf();
    for (let i = 0; i < pdf.numPages; i += 1) {
        await drawPage(i + 1, props.addLimitedAccessPage && i === pdf.numPages - 1);
        if (i === 0) {
            await refreshPageBaseColors();
        }
    }
    pageCount.value = pdf.numPages;
}

onMounted(async () => {
    await drawAllPages();
    await collectWatermarkColours();
    emit('pdf-loaded', Object.values(allTextContent).join(' '));
    if (props.highlightedTextLocation) {
        nextTick(() => {
            highlightText();
        });
    }
});

function clearHighlights (shouldEmit = true): void {
    const highlighted = pdfElement.value?.querySelectorAll('.highlighted');

    highlighted?.forEach((element) => {
        element.classList.remove('highlighted');
    });

    highlights.forEach((highlight) => {
        highlight.remove();
    });

    highlights.length = 0;
    if (!shouldEmit) return;

    emit('clear-highlights');
}

function handleInternalLink (dest: string | PdfDestination): void {
    if (!dest || !pdf) return;

    if (typeof dest === 'string') {
        pdf.getDestination(dest).then((destination) => {
            if (!destination) return;
            navigateToDestination(destination as PdfDestination);
        });
    } else if (Array.isArray(dest)) {
        navigateToDestination(dest);
    }
}

function navigateToDestination (destination: PdfDestination): void {
    if (!destination || destination.length === 0) return;

    const pageRef = destination[0];
    if (!pageRef || typeof pageRef !== 'object' || !('num' in pageRef)) return;

    pdf?.getPageIndex(pageRef as RefProxy).then((pageIndex) => {
        const targetPage = pageIndex + 1;
        const topCoordinate = destination[3];
        scrollToPageWithPosition(targetPage, topCoordinate);
    }).catch(() => {
        findPageByReference(pageRef).then((pageIndex) => {
            if (pageIndex !== -1) {
                const targetPage = pageIndex + 1;
                const topCoordinate = destination[3];
                scrollToPageWithPosition(targetPage, topCoordinate);
            }
        });
    });
}

async function findPageByReference (targetRef: { num: number; gen: number }): Promise<number> {
    if (!pdf) return -1;

    for (let i = 1; i <= pdf.numPages; i += 1) {
        const page = await pdf.getPage(i);
        const pageRef = page.ref;
        if (pageRef && pageRef.num === targetRef.num && pageRef.gen === targetRef.gen) {
            return i - 1;
        }
    }

    return -1;
}

function scrollToPageWithPosition (pageNumber: number, topCoordinate?: number | null): void {
    const pageWrappers = pdfElement.value?.querySelectorAll('.page-wrapper');
    if (!pageWrappers || pageNumber < 1 || pageNumber > pageWrappers.length) return;

    const pageWrapper = pageWrappers[pageNumber - 1] as HTMLElement;
    if (!pageWrapper) return;

    let scrollTop = pageWrapper.offsetTop;

    if (topCoordinate !== null && topCoordinate !== undefined && pdfElement.value) {
        const canvas = pageWrapper.querySelector('canvas');
        if (canvas) {
            const pageIndex = pageNumber - 1;
            const page = pages[pageIndex];
            if (page) {
                const viewport = page.getViewport({ scale: scale.value });
                const pixelY = viewport.height - (topCoordinate * scale.value);
                scrollTop += pixelY + (props.headerHeight ?? 0);
            }
        }
    }

    scrollTo(scrollTop);
}

function scrollTo (top: number, left?: number): void {
    if (props.scrollRef) {
        props.scrollRef.scrollTo({ top, left, behavior: 'smooth' });
    } else if (pdfElement.value) {
        pdfElement.value.scrollTo({ top, left, behavior: 'smooth' });
    }
}

function clearDrawnBox (): void {
    const drawnBox = document.getElementById('box')!;
    if (!drawnBox) return;
    drawnBox.style.height = '0px';
    drawnBox.style.width = '0px';
}

function createPdfLink (annotation: { subtype: string; url?: string; dest?: string | PdfDestination; rect: number[] }, index: number, viewport: pdfjsLib.PageViewport, textLayer: HTMLElement): void {
    if (annotation.subtype !== 'Link') return;

    const link = document.createElement('a');

    if (annotation.url) {
        link.href = annotation.url;
        link.target = '_blank';
        link.title = `Open link: ${annotation.url}`;
    } else if (annotation.dest) {
        link.href = '#';
        link.title = 'Navigate within document';
        link.onclick = (e): boolean => {
            e.preventDefault();
            handleInternalLink(annotation.dest!);
            return false;
        };
    } else {
        return;
    }

    const transform = viewport.transform;
    const rect = pdfjsLib.Util.getAxialAlignedBoundingBox(annotation.rect, transform);

    link.style.position = 'absolute';
    link.style.left = `${rect[0]}px`;
    link.style.top = `${rect[1]}px`;
    link.style.width = `${Math.abs(rect[2] - rect[0])}px`;
    link.style.height = `${Math.abs(rect[3] - rect[1])}px`;
    link.dataset.index = index.toString();
    link.dataset.annotationType = 'link';

    textLayer.appendChild(link);
}

watch(() => uiStore.theme, () => {
    refreshPageBaseColors();
});

watch(() => (props.highlightedTextLocation), async () => {
    highlightText();
});

watch(() => (props.clearContextCounter), () => {
    clearHighlights();
});

async function applyScale (newScale: number, previousScale: number): Promise<void> {
    const scaleDifference = newScale / previousScale;
    const pageWrappers = pdfElement.value?.querySelectorAll('.page-wrapper');

    for (let i = 0; i < pages.length; i += 1) {
        const page = pages[i];
        const canvas = pdfElement.value?.querySelectorAll('canvas')[i];
        const pageWrapper = pageWrappers?.[i] as HTMLElement;
        if (!canvas || !pageWrapper) {
            return;
        }
        page.cleanup();

        const context = canvas.getContext('2d')!;
        const viewport = page.getViewport({ scale: scale.value });

        canvas.height = viewport.height;
        canvas.width = viewport.width;

        canvas.style.height = `${viewport.height}px`;
        canvas.style.width = `${viewport.width}px`;

        const task = page.render({
            canvasContext: context,
            viewport,
        });
        await task.promise;

        const textLayer = pageWrapper.querySelector('.textLayer') as HTMLElement | null;
        if (textLayer) {
            textLayer.innerHTML = '';
            textLayer.style.setProperty('--scale-factor', scale.value.toString());

            textLayer.style.left = `${canvas.offsetLeft}px`;
            textLayer.style.top = `${canvas.offsetTop}px`;
            textLayer.style.height = `${canvas.offsetHeight}px`;
            textLayer.style.width = `${canvas.offsetWidth}px`;

            const textContent = await page.getTextContent();
            if (textContent && textContent.items) {
                if (props.textItems?.[i]) {
                    textContent.items = props.textItems[i] as Array<TextItem>;
                    const defaultFont = (textContent.items.find((item) => (item as TextItem).fontName) as TextItem)?.fontName || 'g_d0_f1';
                    textContent.items.forEach((item) => {
                        (item as TextItem).fontName = defaultFont;
                    });
                }

                const textLayerRenderer = new pdfjsLib.TextLayer({
                    textContentSource: textContent,
                    container: textLayer,
                    viewport,
                });
                await textLayerRenderer.render();
            }
        }

        if (textLayer) {
            const annotations = await page.getAnnotations({ intent: 'any' });
            textLayer.querySelectorAll('a[data-annotation-type="link"]').forEach((link) => {
                link.remove();
            });
            annotations.forEach((annotation, i2) => {
                createPdfLink(annotation, i2, viewport, textLayer);
            });
        }
    }

    const drawnBox = document.getElementById('box')!;
    if (!drawnBox) return;
    drawnBox.classList.add('animate');
    drawnBox.style.width = `${drawnBox.offsetWidth * scaleDifference}px`;
    drawnBox.style.height = `${drawnBox.offsetHeight * scaleDifference}px`;
    drawnBox.style.top = `${drawnBox.offsetTop * scaleDifference}px`;
    drawnBox.style.left = `${drawnBox.offsetLeft * scaleDifference}px`;
    setTimeout(() => {
        drawnBox.classList.remove('animate');
    }, 500);
}

async function setScale (newScale: number): Promise<void> {
    const previousScale = scale.value;
    scale.value = newScale;
    applyScale(newScale, previousScale);
}

window.onresize = async (): Promise<void> => {
    await nextTick();
    if (!pdfElement.value || !pages.length) return;
    fitScale.value = (pdfElement.value!.offsetWidth - PAGE_PADDING) / pages[0].view[2];

    if (isFullScreen.value && !document.fullscreenElement) {
        isFullScreen.value = false;

        await setScale(previousZoomState.value);
        return;
    }
    await setScale(fitScale.value);
};

defineExpose({
    clearDrawnBox,
    download,
    openFullScreen,
    zoomIn,
    zoomOut,
    getCurrentPageNumber,
    scrollToPage,
});
</script>

<style lang="scss" scoped>
.pdf-wrapper {
    height: 100%;
    min-height: 0;
    overflow: auto;
    display: flex;
    flex-direction: column;
    position: relative;
    user-select: text;
    &.drawing {
        cursor: crosshair;

        .textLayer {
            pointer-events: none;
        }
    }

    .pdf {
        position: relative;
        min-height: 0;
        overflow: auto;
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: var(--smallSpacing);

        &.fullScreen {
           overflow-y: auto !important;
        }

        &.draggable {
            cursor: grab;
            user-select: none;

            &.dragging {
                cursor: grabbing;
            }
        }

        :deep(.page-wrapper) {
            &.blur {
                canvas{
                    padding-top: 2rem;
                   filter: blur(8px);
                }
            }
        }

        :deep(.page-details-wrapper) {
            flex: 1 0 auto;
            padding: var(--defaultSpacing);
            display: flex;
            justify-content: space-between;
            align-items: center;

            @include responsive(sm) {
                flex: unset;

                .p-button {
                    min-width: max-content;
                }
            }

            span {
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;

                @include responsive(sm) {
                    white-space: normal;
                }
            }
        }

        &.light :deep(.textLayer) {
            border: 1px solid var(--neutral500);
            box-shadow: 0 6px 18px rgba(0, 0, 0, 0.15);
        }

        a.link {
            position: absolute;
            transition: all 0.5s ease-in-out;
        }
    }

    .block-top,
    .block-bottom,
    .block-left,
    .block-right {
        position: absolute;
        background: rgba(0, 0, 0, 0.7);
        width: 100%;
        transition: top 0.5s, height 0.5s, left 0.5s, width 0.5s;
        z-index: 1;
        backdrop-filter: blur(1.5px);
    }

    canvas {
        border-radius: 0.25rem;
        box-shadow: rgba(0, 0, 0, 0.1) 2px 2px 10px;
        transition: all 0.5s ease-in-out;
    }

    .textLayer {
        position: absolute;
        transition: all 0.5s ease-in-out;
        opacity: 0.3;
    }

    .drawn-box {
        position: absolute;
        background: rgba(0, 0, 0, 0.1);
        border: 1px solid rgba(255, 0, 0, 0.2);
        z-index: 2;
        pointer-events: none;
    }

    .drawn-box.animate {
        transition: all 0.5s ease-in-out;
    }

    .vignette {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        box-shadow: inset 0 0 50px 20px rgba(0, 0, 0, 0.4);
    }

    .highlight-helper {
        position: absolute;
        z-index: 4;
        background: #333;
        border-radius: 0.25rem;
        transform: translate(0, -110%);
        display: none;

        &.active {
            display: block;
        }

        button {
            background: none;
            border: 0;
            color: #fff;
            padding: var(--smallSpacing) var(--defaultSpacing);
            border-radius: 0.25rem;
        }
    }

    .locked-overlay {
        position: absolute;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
        gap: var(--defaultSpacing);
        background-color: rgba(0, 0, 0, 0.5);
        padding: var(--defaultSpacing);
        border-radius: var(--borderRadiusOuter);
        box-shadow: 0 0 10px 0 rgba(0, 0, 0, 0.5);
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 10;

        .locked-text {
            display: flex;
            gap: var(--defaultSpacing);
            align-items: center;
            font-size: var(--largeFontSize);

            svg {
                font-size: var(--extraLargeFontSize);
            }
        }
    }
}
</style>
